import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { portalApi, fetchBlobUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StudioCanvas from "@/components/studio/StudioCanvas";
import ColorPicker from "@/components/studio/ColorPicker";
import { parseFramework, fmtInch, normalizeLabel } from "@/lib/svgUtils";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2, Upload, LogOut, Save, Send, Trash2, Copy, ChevronUp, ChevronDown, ChevronRight,
  Layers, ImageIcon, CheckCircle2, RotateCw, RotateCcw, Undo2, Redo2, Palette, Crosshair, Minus, Plus,
  StretchVertical, StretchHorizontal, Ruler, AlertTriangle, X,
} from "lucide-react";

// Downscale a display image to bound mobile memory (prevents renderer OOM on large art).
// The final print uses the original file by id on the admin side, so this is preview-only.
async function downscaleObjectUrl(objectUrl, maxDim = 1600) {
  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth, h = img.naturalHeight;
      if (!w || !h || Math.max(w, h) <= maxDim) return resolve(objectUrl);
      const scale = maxDim / Math.max(w, h);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (!blob) return resolve(objectUrl);
        URL.revokeObjectURL(objectUrl);
        resolve(URL.createObjectURL(blob));
      }, "image/png");
    };
    img.onerror = () => resolve(objectUrl);
    img.src = objectUrl;
  });
}

const DISPLAY_MAX_DIM = (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches) ? 1100 : 1600;

function useIsDesktop() {
  const getMatch = () => (typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true);
  const [isDesktop, setIsDesktop] = useState(getMatch);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
}

export default function DesignStudio() {
  const navigate = useNavigate();
  const { logoutPortal } = useAuth();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [product, setProduct] = useState(null);
  const [files, setFiles] = useState([]);
  const [blobMap, setBlobMap] = useState({});
  const blobMapRef = useRef({});
  blobMapRef.current = blobMap;
  const [graphics, setGraphics] = useState([]);
  const [activePartId, setActivePartId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [saveState, setSaveState] = useState("saved");
  const [uploading, setUploading] = useState(false);
  const [uniformScale, setUniformScale] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [unitsPerInch, setUnitsPerInch] = useState(null);
  const [partBoxes, setPartBoxes] = useState({});
  const [unit, setUnit] = useState("in");
  const perUnit = unitsPerInch ? (unit === "cm" ? unitsPerInch / 2.54 : unitsPerInch) : null;
  const [colorOpen, setColorOpen] = useState(false);
  const [partsOpen, setPartsOpen] = useState(true);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [tosAgreed, setTosAgreed] = useState(false);
  const [mobilePanel, setMobilePanel] = useState(null);
  const isDesktop = useIsDesktop();
  const [submitting, setSubmitting] = useState(false);
  const [partColors, setPartColors] = useState({});
  const [duplications, setDuplications] = useState({});
  const duplicationsRef = useRef({});
  duplicationsRef.current = duplications;
  const [recentColors, setRecentColors] = useState([]);
  const partColorsRef = useRef({});
  partColorsRef.current = partColors;
  const [guideColor, setGuideColor] = useState("#111111");
  const guideColorRef = useRef("#111111");
  guideColorRef.current = guideColor;
  const dirty = useRef(false);

  const submitted = order?.status === "submitted";

  // ---- Panel duplication (bracketed "[duplicates: X]" rules) ----
  const clientParts = useMemo(() => parseFramework(product?.framework_svg).parts, [product]);
  // Resolve each part's duplicate rule to a concrete source part id (matched by normalized label).
  const dupRules = useMemo(() => {
    const byNorm = {};
    clientParts.forEach((p) => { byNorm[normalizeLabel(p.label)] = p; });
    const map = {};
    clientParts.forEach((p) => {
      if (!p.duplicates) return;
      const src = byNorm[normalizeLabel(p.duplicates)];
      if (src && src.id !== p.id) map[p.id] = { sourceId: src.id, sourceLabel: src.label, label: p.label };
    });
    return map;
  }, [clientParts]);
  const partsView = useMemo(
    () => (clientParts.length ? clientParts : (product?.parts || [])).slice().sort((a, b) => a.label.localeCompare(b.label)),
    [clientParts, product]
  );
  const isDupTarget = useCallback((pid) => Object.prototype.hasOwnProperty.call(duplications, pid), [duplications]);
  const lockedPartIds = useMemo(() => Object.keys(duplications), [duplications]);

  // Live mirror graphics: clone each source part's graphics into its target part,
  // translated by the center offset between the two part bounding boxes.
  const mirrors = useMemo(() => {
    const out = [];
    Object.entries(duplications).forEach(([targetId, sourceId]) => {
      const sBox = partBoxes[sourceId];
      const tBox = partBoxes[targetId];
      let dx = 0, dy = 0;
      if (sBox && tBox) {
        dx = (tBox.x + tBox.width / 2) - (sBox.x + sBox.width / 2);
        dy = (tBox.y + tBox.height / 2) - (sBox.y + sBox.height / 2);
      }
      graphics.filter((g) => g.partId === sourceId).forEach((g) => {
        out.push({
          ...g,
          id: `${g.id}::dup::${targetId}`,
          partId: targetId,
          cx: g.cx + dx, cy: g.cy + dy,
          originX: (g.originX ?? g.cx) + dx, originY: (g.originY ?? g.cy) + dy,
          dup: true,
        });
      });
    });
    return out;
  }, [duplications, graphics, partBoxes]);

  // What the canvas/exports see: base graphics (hiding a duplicated target's own art) + mirrors.
  const effectiveGraphics = useMemo(
    () => [...graphics.filter((g) => !isDupTarget(g.partId)), ...mirrors],
    [graphics, mirrors, isDupTarget]
  );
  const effectivePartColors = useMemo(() => {
    const out = {};
    Object.entries(partColors).forEach(([pid, c]) => { if (!isDupTarget(pid)) out[pid] = c; });
    Object.entries(duplications).forEach(([targetId, sourceId]) => { if (partColors[sourceId]) out[targetId] = partColors[sourceId]; });
    return out;
  }, [partColors, duplications, isDupTarget]);
  const effectivePartColorsRef = useRef({});
  effectivePartColorsRef.current = effectivePartColors;

  const toggleDuplicate = (targetId) => {
    const rule = dupRules[targetId];
    if (!rule) return;
    dirty.current = true;
    setDuplications((d) => {
      const next = { ...d };
      if (next[targetId]) { delete next[targetId]; toast.success(`${rule.label} unlinked — edit it independently`); }
      else { next[targetId] = rule.sourceId; toast.success(`${rule.label} now mirrors ${rule.sourceLabel}`); }
      return next;
    });
    if (activePartId === targetId) setSelectedId(null);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await portalApi.get("/portal/me");
      setOrder(data.order);
      setProduct(data.product);
      setFiles(data.files || []);
      const map = {};
      await Promise.all(
        (data.files || []).map(async (f) => {
          try { map[f.id] = await downscaleObjectUrl(await fetchBlobUrl(f.id), DISPLAY_MAX_DIM); } catch (e) {}
        })
      );
      setBlobMap(map);
      const design = data.order?.design || {};
      const loadedDup = design.duplications || {};
      const dupTargetIds = new Set(Object.keys(loadedDup));
      const g = (design.graphics || [])
        .filter((x) => !x.dup && !dupTargetIds.has(x.partId))
        .map((x) => ({ ...x, url: map[x.fileId], originX: x.originX ?? x.cx, originY: x.originY ?? x.cy }));
      setGraphics(g);
      pastRef.current = []; futureRef.current = []; lastRecordRef.current = 0;
      const baseColors = {};
      Object.entries(design.partColors || {}).forEach(([pid, c]) => { if (!dupTargetIds.has(pid)) baseColors[pid] = c; });
      setPartColors(baseColors);
      setDuplications(loadedDup);
      setGuideColor(design.guideColor || "#111111");
      if (data.product?.parts?.length) setActivePartId(data.product.parts[0].id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      if (err.response?.status === 401) { logoutPortal(); navigate("/"); }
    } finally {
      setLoading(false);
    }
  }, [logoutPortal, navigate]);

  useEffect(() => { load(); }, [load]);

  // Release blob URLs when leaving the studio to avoid memory buildup on mobile.
  useEffect(() => () => {
    Object.values(blobMapRef.current).forEach((u) => { try { URL.revokeObjectURL(u); } catch (e) {} });
  }, []);

  const persist = useCallback(async (list) => {
    const payload = list.map(({ url, ...rest }) => rest);
    await portalApi.put("/portal/design", { graphics: payload, partColors: effectivePartColorsRef.current, guideColor: guideColorRef.current, duplications: duplicationsRef.current });
  }, []);

  // Debounced autosave
  useEffect(() => {
    if (loading || submitted || !dirty.current) return;
    setSaveState("saving");
    const t = setTimeout(async () => {
      try { await persist(effectiveGraphics); setSaveState("saved"); }
      catch (e) { setSaveState("error"); }
    }, 1000);
    return () => clearTimeout(t);
  }, [graphics, partColors, guideColor, duplications, effectiveGraphics, loading, submitted, persist]);

  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const lastRecordRef = useRef(0);
  const [, setHistVer] = useState(0);
  const recordHistory = (coalesce = false) => {
    const now = Date.now();
    if (coalesce && now - lastRecordRef.current < 600 && pastRef.current.length) { lastRecordRef.current = now; return; }
    lastRecordRef.current = now;
    pastRef.current.push(graphics.map((g) => ({ ...g })));
    if (pastRef.current.length > 50) pastRef.current.shift();
    futureRef.current = [];
    setHistVer((v) => v + 1);
  };
  const updateGraphics = (list, coalesce = false) => { dirty.current = true; recordHistory(coalesce); setGraphics(list); };
  // Canvas edits operate on the effective list (base-minus-hidden + mirrors). Reconcile back to
  // base: drop mirrors, and re-attach the hidden graphics of currently-duplicated target parts.
  const handleCanvasChange = (list) => {
    dirty.current = true;
    recordHistory(true);
    const fromCanvas = list.filter((g) => !g.dup);
    setGraphics((prev) => [...prev.filter((g) => isDupTarget(g.partId)), ...fromCanvas]);
  };
  const undo = () => {
    if (!pastRef.current.length) return;
    futureRef.current.push(graphics.map((g) => ({ ...g })));
    setGraphics(pastRef.current.pop());
    setSelectedId(null); dirty.current = true; lastRecordRef.current = 0;
    setHistVer((v) => v + 1);
  };
  const redo = () => {
    if (!futureRef.current.length) return;
    pastRef.current.push(graphics.map((g) => ({ ...g })));
    setGraphics(futureRef.current.pop());
    setSelectedId(null); dirty.current = true; lastRecordRef.current = 0;
    setHistVer((v) => v + 1);
  };
  const canUndo = pastRef.current.length > 0;
  const canRedo = futureRef.current.length > 0;
  const setActivePartColor = (color) => {
    if (!activePartId) { toast.error("Select a part first"); return; }
    if (isDupTarget(activePartId)) { toast.error("This panel mirrors another — uncheck Duplicate to color it"); return; }
    dirty.current = true;
    setPartColors((pc) => {
      const next = { ...pc };
      if (color) next[activePartId] = color; else delete next[activePartId];
      return next;
    });
  };
  const commitRecent = (color) => {
    if (!color) return;
    setRecentColors((r) => [color, ...r.filter((c) => c !== color)].slice(0, 8));
  };
  const chooseGuideColor = (hex) => { dirty.current = true; setGuideColor(hex); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await portalApi.post("/portal/graphics", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = await downscaleObjectUrl(await fetchBlobUrl(data.id), DISPLAY_MAX_DIM);
      setBlobMap((m) => ({ ...m, [data.id]: url }));
      setFiles((f) => [...f, { id: data.id, original_filename: data.original_filename }]);
      toast.success("Graphic uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const addToActivePart = (f) => {
    if (!activePartId) { toast.error("Select a part first"); return; }
    if (isDupTarget(activePartId)) { toast.error("This panel mirrors another — uncheck Duplicate to edit it"); return; }
    canvasRef.current?.addGraphicToPart({ fileId: f.id, url: blobMap[f.id], filename: f.original_filename }, activePartId);
  };

  const saveDraft = async () => {
    try { await persist(effectiveGraphics); setSaveState("saved"); toast.success("Draft saved"); }
    catch (e) { toast.error("Could not save"); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = effectiveGraphics.map(({ url, ...rest }) => rest);
      await portalApi.post("/portal/submit", { graphics: payload, partColors: effectivePartColorsRef.current, guideColor: guideColorRef.current, duplications: duplicationsRef.current });
      setOrder((o) => ({ ...o, status: "submitted" }));
      setConfirmSubmit(false);
      toast.success("Design submitted to the atelier!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const selected = graphics.find((g) => g.id === selectedId);
  const setSelPatch = (patch) => updateGraphics(graphics.map((g) => (g.id === selectedId ? { ...g, ...patch } : g)), true);
  const rotateSel = (delta) => {
    if (!selected) return;
    setSelPatch({ rotation: Math.round((((selected.rotation || 0) + delta) % 360 + 360) % 360) });
  };
  // Snap to the NEXT 45° step (0, 45, 90, ...) regardless of the current angle.
  const rotateSnap45 = () => {
    if (!selected) return;
    const cur = (((selected.rotation || 0) % 360) + 360) % 360;
    const next = ((Math.floor((cur + 0.5) / 45) + 1) * 45) % 360;
    setSelPatch({ rotation: next });
  };
  const deleteSel = () => { updateGraphics(graphics.filter((g) => g.id !== selectedId)); setSelectedId(null); };
  useEffect(() => {
    const nudges = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const onKeyDown = (e) => {
      const t = e.target;
      const tag = t?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
      if (submitted) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && (e.key === "z" || e.key === "Z")) { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if (mod && (e.key === "y" || e.key === "Y")) { e.preventDefault(); redo(); return; }
      if (!selectedId) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        deleteSel();
        return;
      }
      const dir = nudges[e.key];
      if (dir) {
        const sel = graphics.find((g) => g.id === selectedId);
        if (!sel) return;
        e.preventDefault();
        // 0.1" per press, 1" with Shift (falls back to units when uncalibrated)
        const stepUnits = unitsPerInch ? (e.shiftKey ? 1 : 0.1) * unitsPerInch : (e.shiftKey ? 50 : 5);
        setSelPatch({ cx: sel.cx + dir[0] * stepUnits, cy: sel.cy + dir[1] * stepUnits });
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, submitted, graphics, unitsPerInch]);
  const duplicateSel = () => {
    if (!selected) return;
    const copy = { ...selected, id: crypto.randomUUID(), cx: selected.cx + 20, cy: selected.cy + 20, originX: selected.cx + 20, originY: selected.cy + 20 };
    updateGraphics([...graphics, copy]);
    setSelectedId(copy.id);
  };
  const snapFill = (axis) => {
    if (!selected) return;
    const box = canvasRef.current?.getParts?.()?.[selected.partId];
    if (!box) { toast.error("Part bounds unavailable"); return; }
    if (axis === "vertical") {
      const h = box.height;
      const patch = { h, cy: box.y + box.height / 2 };
      if (uniformScale && selected.h) patch.w = Math.max(16, selected.w * (h / selected.h));
      setSelPatch(patch);
    } else {
      const w = box.width;
      const patch = { w, cx: box.x + box.width / 2 };
      if (uniformScale && selected.w) patch.h = Math.max(16, selected.h * (w / selected.w));
      setSelPatch(patch);
    }
  };
  const setInch = (axis, val) => {
    const v = parseFloat(val);
    if (!selected || !perUnit || !(v > 0)) return;
    const units = v * perUnit;
    if (axis === "w") {
      const patch = { w: units };
      if (uniformScale && selected.w) patch.h = selected.h * (units / selected.w);
      setSelPatch(patch);
    } else {
      const patch = { h: units };
      if (uniformScale && selected.h) patch.w = selected.w * (units / selected.h);
      setSelPatch(patch);
    }
  };
  const setPos = (axis, val) => {
    const v = parseFloat(val);
    if (!selected || !perUnit || isNaN(v)) return;
    const ox = selected.originX ?? selected.cx;
    const oy = selected.originY ?? selected.cy;
    // X: right positive · Y: up positive (canvas y grows downward, so invert)
    if (axis === "x") setSelPatch({ cx: ox + v * perUnit });
    else setSelPatch({ cy: oy - v * perUnit });
  };
  const resetPos = () => {
    if (!selected) return;
    setSelPatch({ cx: selected.originX ?? selected.cx, cy: selected.originY ?? selected.cy });
  };
  const reorder = (dir) => {
    const idx = graphics.findIndex((g) => g.id === selectedId);
    if (idx < 0) return;
    const next = [...graphics];
    const target = dir === "up" ? Math.min(next.length - 1, idx + 1) : Math.max(0, idx - 1);
    [next[idx], next[target]] = [next[target], next[idx]];
    updateGraphics(next);
  };

  const graphicsWithUrl = useMemo(() => effectiveGraphics.map((g) => ({ ...g, url: blobMap[g.fileId] })), [effectiveGraphics, blobMap]);
  const hasGuides = useMemo(() => !!parseFramework(product?.framework_svg).guides, [product]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F8F6F0]"><Loader2 className="h-6 w-6 animate-spin text-[#C85A32]" /></div>;
  }

  const saveLabel = { saving: "Saving…", saved: "All changes saved", error: "Save failed" }[saveState];

  return (
    <div className="h-[100dvh] flex flex-col bg-[#EFECE6] overflow-hidden">
      {/* Header */}
      <header className="flex items-center justify-between px-4 lg:px-6 py-3 bg-[#161616] text-[#F8F6F0] shrink-0 gap-2">
        <div className="flex items-center gap-3 lg:gap-6 min-w-0">
          <div className="min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C867B] truncate">Order {order?.order_number}</p>
            <h1 className="font-serif text-base lg:text-lg leading-tight truncate">{product?.name}</h1>
          </div>
          {order?.customer_name && <span className="hidden sm:inline text-sm text-[#8C867B] truncate">{order.customer_name}</span>}
        </div>
        <div className="flex items-center gap-2 lg:gap-3 shrink-0">
          {!submitted && <span className="hidden lg:inline font-mono text-[11px] text-[#8C867B]" data-testid="studio-save-status">{saveLabel}</span>}
          {submitted ? (
            <span className="flex items-center gap-2 text-sm text-[#2D6A4F] bg-[#2D6A4F]/15 rounded-full px-3 lg:px-4 py-1.5">
              <CheckCircle2 className="h-4 w-4" /> Submitted
            </span>
          ) : (
            <>
              <Button data-testid="studio-save-draft-button" onClick={saveDraft} variant="ghost" className="text-[#F8F6F0] hover:bg-white/10 h-9 px-2 lg:px-4">
                <Save className="h-4 w-4 lg:mr-2" /> <span className="hidden lg:inline">Save</span>
              </Button>
              <Button data-testid="studio-submit-design-button" onClick={() => setConfirmSubmit(true)} className="h-9 rounded-full bg-[#C85A32] hover:bg-[#B04B26] px-3 lg:px-4">
                <Send className="h-4 w-4 lg:mr-2" /> <span className="hidden sm:inline">Submit Final Craft</span><span className="sm:hidden">Submit</span>
              </Button>
            </>
          )}
          <button data-testid="portal-logout-button" onClick={() => { logoutPortal(); navigate("/"); }} className="text-[#8C867B] hover:text-[#F8F6F0]">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <AlertDialog open={confirmSubmit} onOpenChange={(o) => { if (!submitting) { setConfirmSubmit(o); if (o) setTosAgreed(false); } }}>
        <AlertDialogContent className="bg-[#F8F6F0]" data-testid="finalize-design-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Finalize design?</AlertDialogTitle>
            <AlertDialogDescription>
              By confirming, you are submitting this design to print and no more edits can be made.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="rounded-lg border border-[#E2DEC9] bg-white px-4 py-3 max-h-56 overflow-y-auto" data-testid="finalize-tos">
            <h4 className="font-serif text-sm font-semibold text-[#2B2824] mb-1.5">Intellectual Property, Customer Liability &amp; Marketing License</h4>
            <p className="text-xs leading-relaxed text-[#5C5852]">
              By submitting your order, you guarantee that you own or have full legal permission to use all images, text, and designs uploaded. You agree that you are solely responsible for how you use, distribute, or display the physical products we manufacture for you. Furthermore, you agree to indemnify, defend, and hold our company harmless from any legal claims, damages, or costs (including attorney fees) arising from copyright infringement, trademark violations, defamation, or any harmful or illegal use of the products we create based on your submission. We reserve the right to refuse or cancel any order at any time, for any reason, at our sole discretion.
            </p>
            <p className="text-xs leading-relaxed text-[#5C5852] mt-2">
              By submitting, you also grant us a perpetual, worldwide, and royalty-free license to feature, photograph, and display the finished physical products we manufacture for you across our business platforms, including social media feeds, websites, marketing materials, and digital portfolios.
            </p>
          </div>

          <label className="flex items-start gap-2.5 mt-1 cursor-pointer select-none" data-testid="finalize-tos-row">
            <Checkbox
              checked={tosAgreed}
              onCheckedChange={(v) => setTosAgreed(!!v)}
              disabled={submitting}
              className="mt-0.5 data-[state=checked]:bg-[#C85A32] data-[state=checked]:border-[#C85A32]"
              data-testid="finalize-tos-checkbox"
            />
            <span className="text-xs text-[#2B2824]">I have read and agree to the terms above.</span>
          </label>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting} data-testid="finalize-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); submit(); }} disabled={submitting || !tosAgreed} className="bg-[#C85A32] hover:bg-[#B04B26] text-[#F8F6F0] disabled:opacity-50 disabled:pointer-events-none" data-testid="finalize-confirm-button">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm & Submit"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex-1 flex overflow-hidden">
        {/* Left tray — desktop column / mobile bottom sheet */}
        <aside
          className={`bg-[#F8F6F0] overflow-y-auto transition-transform duration-300 fixed inset-x-0 bottom-0 z-50 max-h-[80vh] border-t border-[#E2DEC9] rounded-t-2xl shadow-2xl pb-[env(safe-area-inset-bottom)] ${mobilePanel === "graphics" ? "translate-y-0" : "translate-y-full"} lg:static lg:inset-auto lg:bottom-auto lg:z-auto lg:max-h-none lg:w-72 lg:border-t-0 lg:border-r lg:rounded-none lg:shadow-none lg:translate-y-0 lg:flex lg:flex-col lg:shrink-0`}
          data-testid="studio-left-tray"
        >
          <div className="flex items-center justify-between p-3 border-b border-[#E2DEC9] lg:hidden">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8C867B] flex items-center gap-2"><ImageIcon className="h-3 w-3" /> Your Graphics</span>
            <button onClick={() => setMobilePanel(null)} data-testid="studio-mobile-close-graphics" className="text-[#8C867B]"><X className="h-5 w-5" /></button>
          </div>
          <div className="p-4 border-b border-[#E2DEC9]">
            <p className="hidden lg:flex font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C867B] mb-3 items-center gap-2"><ImageIcon className="h-3 w-3" /> Your Graphics</p>
            <input ref={fileInputRef} type="file" accept="image/*,.svg" hidden onChange={handleUpload} data-testid="studio-upload-graphic-input" />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || submitted}
              variant="outline"
              className="w-full border-dashed border-[#C8C2AC] bg-white hover:bg-[#EFECE6] h-11"
              data-testid="studio-upload-graphic-button"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" /> Upload graphic</>}
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 grid grid-cols-2 gap-3 content-start">
            {files.length === 0 && <p className="col-span-2 text-xs text-[#8C867B] text-center mt-6">No graphics yet. Upload PNG, JPG or SVG files.</p>}
            {files.map((f) => (
              <div
                key={f.id}
                draggable={!submitted}
                onDragStart={(e) => e.dataTransfer.setData("application/json", JSON.stringify({ fileId: f.id, url: blobMap[f.id], filename: f.original_filename }))}
                onClick={() => { if (!submitted) { addToActivePart(f); setMobilePanel(null); } }}
                className="group relative aspect-square bg-white rounded-md border border-[#E2DEC9] overflow-hidden cursor-grab hover:border-[#C85A32] transition-colors"
                data-testid="studio-graphic-item"
                title={f.original_filename}
              >
                {blobMap[f.id] ? <img src={blobMap[f.id]} alt={f.original_filename} className="w-full h-full object-contain p-1 no-native-drag" /> : <Loader2 className="h-4 w-4 animate-spin m-auto mt-6 text-[#8C867B]" />}
              </div>
            ))}
          </div>
        </aside>

        {/* Center canvas */}
        <main className="flex-1 flex flex-col overflow-hidden bg-[#EFECE6]">
          <div className="flex items-center gap-2 px-4 py-2 bg-[#F8F6F0] border-b border-[#E2DEC9] shrink-0">
            <Checkbox
              id="uniform-scale"
              checked={uniformScale}
              onCheckedChange={(v) => setUniformScale(!!v)}
              disabled={submitted}
              data-testid="studio-uniform-scale-checkbox"
              className="data-[state=checked]:bg-[#C85A32] data-[state=checked]:border-[#C85A32]"
            />
            <Label htmlFor="uniform-scale" className="text-xs text-[#5C5852] cursor-pointer select-none">Uniform Scaling</Label>
            <span className="hidden sm:inline text-[11px] text-[#8C867B] ml-1">— keeps graphics in proportion when resizing</span>
          </div>
          <div className="flex-1 craft-grid overflow-hidden flex items-stretch justify-center p-4 relative touch-none overscroll-contain" data-testid="studio-canvas-area">
            {unitsPerInch && (
              <div className="absolute left-4 top-4 z-10 flex items-center gap-2">
                <div className="flex items-center bg-white/90 backdrop-blur rounded-full border border-[#E2DEC9] shadow-sm p-1" data-testid="studio-unit-toggle">
                  <button onClick={() => setUnit("in")} className={`px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider transition-colors ${unit === "in" ? "bg-[#C85A32] text-white" : "text-[#5C5852] hover:text-[#C85A32]"}`} data-testid="studio-unit-in">in</button>
                  <button onClick={() => setUnit("cm")} className={`px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider transition-colors ${unit === "cm" ? "bg-[#C85A32] text-white" : "text-[#5C5852] hover:text-[#C85A32]"}`} data-testid="studio-unit-cm">cm</button>
                </div>
                {/* Quick actions — Undo/Redo on all sizes; trash/rotate are mobile-only (desktop has them in the side panel) */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={deleteSel}
                    disabled={!selected || submitted}
                    className="lg:hidden h-9 w-9 flex items-center justify-center rounded-full bg-white/90 backdrop-blur border border-[#9E2A2B]/40 shadow-sm text-[#9E2A2B] active:bg-[#9E2A2B] active:text-white transition-colors disabled:opacity-40 disabled:pointer-events-none"
                    aria-label="Delete selected graphic"
                    title="Delete selected graphic"
                    data-testid="studio-mobile-delete-graphic"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={undo}
                    disabled={!canUndo || submitted}
                    className="h-9 px-3 flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur border border-[#E2DEC9] shadow-sm text-[#5C5852] text-[11px] font-mono uppercase tracking-wider hover:text-[#C85A32] active:bg-[#C85A32] active:text-white transition-colors disabled:opacity-40 disabled:pointer-events-none"
                    aria-label="Undo"
                    title="Undo (Ctrl+Z)"
                    data-testid="studio-undo"
                  >
                    <Undo2 className="h-3.5 w-3.5" /> Undo
                  </button>
                  <button
                    onClick={redo}
                    disabled={!canRedo || submitted}
                    className="h-9 px-3 flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur border border-[#E2DEC9] shadow-sm text-[#5C5852] text-[11px] font-mono uppercase tracking-wider hover:text-[#C85A32] active:bg-[#C85A32] active:text-white transition-colors disabled:opacity-40 disabled:pointer-events-none"
                    aria-label="Redo"
                    title="Redo (Ctrl+Y)"
                    data-testid="studio-redo"
                  >
                    <Redo2 className="h-3.5 w-3.5" /> Redo
                  </button>
                  <button
                    onClick={rotateSnap45}
                    disabled={!selected || submitted}
                    className="lg:hidden h-9 w-9 flex items-center justify-center rounded-full bg-white/90 backdrop-blur border border-[#E2DEC9] shadow-sm text-[#5C5852] active:bg-[#C85A32] active:text-white transition-colors disabled:opacity-40 disabled:pointer-events-none"
                    aria-label="Rotate to next 45°"
                    title="Rotate to next 45°"
                    data-testid="studio-mobile-rotate-45"
                  >
                    <RotateCw className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
            <div className="w-full h-full">
              <StudioCanvas
                ref={canvasRef}
                data-testid="studio-canvas-workbench"
                svgString={product?.framework_svg}
                graphics={graphicsWithUrl}
                onChange={handleCanvasChange}
                selectedId={selectedId}
                setSelectedId={setSelectedId}
                activePartId={activePartId}
                setActivePartId={setActivePartId}
                editable={!submitted}
                uniformScale={uniformScale}
                partColors={effectivePartColors}
                guideColor={guideColor}
                zoom={zoom}
                onZoom={setZoom}
                onCalibrate={setUnitsPerInch}
                onParts={setPartBoxes}
                unit={unit}
                lockedPartIds={lockedPartIds}
                className="drop-shadow-sm"
              />
            </div>
            <div className="absolute right-4 bottom-4 w-12 h-56 flex flex-col items-center justify-between bg-white/90 backdrop-blur rounded-full py-3 border border-[#E2DEC9] shadow-sm" data-testid="studio-zoom-control">
              <button
                onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))}
                disabled={zoom >= 4}
                className="w-9 h-9 rounded-full border border-[#C8C2AC] flex items-center justify-center text-[#5C5852] hover:text-[#C85A32] hover:border-[#C85A32] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="studio-zoom-in"
                title="Zoom in"
              ><Plus className="h-4 w-4" /></button>

              <span className="font-mono text-[11px] text-[#5C5852]" data-testid="studio-zoom-value">{Math.round(zoom * 100)}%</span>

              <button
                onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))}
                disabled={zoom <= 1}
                className="w-9 h-9 rounded-full border border-[#C8C2AC] flex items-center justify-center text-[#5C5852] hover:text-[#C85A32] hover:border-[#C85A32] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="studio-zoom-out"
                title="Zoom out"
              ><Minus className="h-4 w-4" /></button>

              <button
                onClick={() => setZoom(1)}
                disabled={zoom === 1}
                className="font-mono text-[9px] uppercase tracking-wider text-[#C85A32] hover:underline disabled:opacity-30 disabled:no-underline"
                data-testid="studio-zoom-reset"
              >Reset</button>
            </div>
          </div>
        </main>

        {/* Right panel — desktop column / mobile bottom sheet */}
        <aside
          className={`bg-[#F8F6F0] overflow-y-auto transition-transform duration-300 fixed inset-x-0 bottom-0 z-50 max-h-[80vh] border-t border-[#E2DEC9] rounded-t-2xl shadow-2xl pb-[env(safe-area-inset-bottom)] ${mobilePanel === "options" ? "translate-y-0" : "translate-y-full"} lg:static lg:inset-auto lg:bottom-auto lg:z-auto lg:max-h-none lg:w-80 lg:border-t-0 lg:border-l lg:rounded-none lg:shadow-none lg:translate-y-0 lg:flex lg:flex-col lg:shrink-0`}
          data-testid="studio-right-panel"
        >
          <div className="flex items-center justify-between p-3 border-b border-[#E2DEC9] lg:hidden">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8C867B] flex items-center gap-2"><Layers className="h-3 w-3" /> Design Tools</span>
            <button onClick={() => setMobilePanel(null)} data-testid="studio-mobile-close-options" className="text-[#8C867B]"><X className="h-5 w-5" /></button>
          </div>
          <div className="p-4 border-b border-[#E2DEC9]" data-testid="studio-product-parts-section">
            <button
              onClick={() => setPartsOpen((o) => !o)}
              className="w-full flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C867B] hover:text-[#C85A32] transition-colors"
              data-testid="studio-product-parts-toggle"
              aria-expanded={partsOpen}
            >
              <span className="flex items-center gap-2"><Layers className="h-3 w-3" /> Product Parts</span>
              <ChevronRight className={`h-3.5 w-3.5 transition-transform ${partsOpen ? "rotate-90" : ""}`} />
            </button>
            {partsOpen && (
              <div className="mt-3" data-testid="studio-product-parts-body">
                <div className="space-y-1.5">
                  {partsView.map((p) => {
                    const rule = dupRules[p.id];
                    const active = !!duplications[p.id];
                    return (
                      <div key={p.id} className="space-y-1">
                        <button
                          onClick={() => setActivePartId(p.id)}
                          data-testid={`studio-part-selector-${p.id}`}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${activePartId === p.id ? "bg-[#C85A32] text-white" : "bg-white border border-[#E2DEC9] text-[#5C5852] hover:border-[#C85A32]"}`}
                        >
                          <span className="flex items-center justify-between gap-2">
                            <span>{p.label}</span>
                            {unitsPerInch && partBoxes[p.id] && (
                              <span className="font-mono text-[10px] opacity-75 whitespace-nowrap" data-testid={`studio-part-size-${p.id}`}>
                                {fmtInch(partBoxes[p.id].width / perUnit)} × {fmtInch(partBoxes[p.id].height / perUnit)} {unit}
                              </span>
                            )}
                          </span>
                        </button>
                        {rule && (
                          <label
                            className={`flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-md border text-xs transition-colors ${submitted ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${active ? "bg-[#2D6A4F]/10 border-[#2D6A4F]/40 text-[#2D6A4F]" : "bg-white border-[#E2DEC9] text-[#5C5852] hover:border-[#C85A32]"}`}
                            data-testid={`studio-duplicate-row-${p.id}`}
                          >
                            <Checkbox
                              checked={active}
                              disabled={submitted}
                              onCheckedChange={() => toggleDuplicate(p.id)}
                              data-testid={`studio-duplicate-checkbox-${p.id}`}
                              className="data-[state=checked]:bg-[#2D6A4F] data-[state=checked]:border-[#2D6A4F]"
                            />
                            <span className="flex items-center gap-1">
                              <Copy className="h-3 w-3" /> Duplicate from <b className="font-semibold">{rule.sourceLabel}</b>
                            </span>
                          </label>
                        )}
                      </div>
                    );
                  })}
                  {!partsView.length && <p className="text-xs text-[#8C867B]">This framework has no labeled parts.</p>}
                </div>
                {unitsPerInch ? (
                  <span data-testid="studio-calibration-badge" className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full bg-[#2D6A4F]/12 text-[#2D6A4F] text-[10px] font-mono uppercase tracking-wider">
                    <Ruler className="h-3 w-3" /> Scale calibrated · 1 in = {Math.round(unitsPerInch)} u
                  </span>
                ) : (product?.parts?.length ? (
                  <span data-testid="studio-calibration-badge" className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full bg-[#B0413E]/12 text-[#B0413E] text-[10px] font-mono uppercase tracking-wider" title='Add a 1×1 inch square named "measurement" to enable true inch sizing.'>
                    <AlertTriangle className="h-3 w-3" /> Not calibrated
                  </span>
                ) : null)}
              </div>
            )}
          </div>

          {selected && !submitted && (
            <div className="p-4 border-b border-[#E2DEC9] space-y-4" data-testid="studio-selected-controls">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C867B]">Selected Graphic</p>
              {unitsPerInch && (
                <div data-testid="studio-inch-size">
                  <p className="text-xs text-[#5C5852] mb-1.5">Size ({unit === "cm" ? "cm" : "inches"})</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-[#8C867B] mb-1">Width</label>
                      <Input type="number" min="0.1" step="0.1" value={fmtInch(selected.w / perUnit)} onChange={(e) => setInch("w", e.target.value)} className="h-8 bg-white border-[#C8C2AC]" data-testid="studio-inch-width-input" />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-[#8C867B] mb-1">Height</label>
                      <Input type="number" min="0.1" step="0.1" value={fmtInch(selected.h / perUnit)} onChange={(e) => setInch("h", e.target.value)} className="h-8 bg-white border-[#C8C2AC]" data-testid="studio-inch-height-input" />
                    </div>
                  </div>
                </div>
              )}
              {unitsPerInch && (
                <div data-testid="studio-position-xy">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs text-[#5C5852]">Position ({unit === "cm" ? "cm" : "inches"}) · from center</p>
                    <button onClick={resetPos} className="text-[10px] font-mono uppercase tracking-wider text-[#C85A32] hover:underline" data-testid="studio-reset-position">Reset</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-[#8C867B] mb-1">X</label>
                      <Input type="number" step="0.1" value={fmtInch((selected.cx - (selected.originX ?? selected.cx)) / perUnit)} onChange={(e) => setPos("x", e.target.value)} className="h-8 bg-white border-[#C8C2AC]" data-testid="studio-pos-x-input" />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-[#8C867B] mb-1">Y</label>
                      <Input type="number" step="0.1" value={fmtInch(((selected.originY ?? selected.cy) - selected.cy) / perUnit)} onChange={(e) => setPos("y", e.target.value)} className="h-8 bg-white border-[#C8C2AC]" data-testid="studio-pos-y-input" />
                    </div>
                  </div>
                </div>
              )}
              <div>
                <p className="text-xs text-[#5C5852] mb-1.5">Snap Fill</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" onClick={() => snapFill("vertical")} className="border-[#C8C2AC]" data-testid="studio-snap-fill-vertical" title="Snap Fill Vertical"><StretchVertical className="h-4 w-4 mr-1" /> Vertical</Button>
                  <Button variant="outline" size="sm" onClick={() => snapFill("horizontal")} className="border-[#C8C2AC]" data-testid="studio-snap-fill-horizontal" title="Snap Fill Horizontal"><StretchHorizontal className="h-4 w-4 mr-1" /> Horizontal</Button>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs text-[#5C5852] mb-1"><span className="flex items-center gap-1"><RotateCw className="h-3 w-3" /> Rotation</span><span className="font-mono">{selected.rotation || 0}°</span></div>
                <Slider value={[((selected.rotation || 0) % 360 + 360) % 360]} min={0} max={359} step={1} onValueChange={(v) => setSelPatch({ rotation: v[0] })} data-testid="studio-rotation-slider" />
                <div className="grid grid-cols-4 gap-1.5 mt-2">
                  <Button variant="outline" size="sm" onClick={() => rotateSel(-90)} className="border-[#C8C2AC] px-0" data-testid="studio-rotate-minus-90" title="Rotate 90° left"><RotateCcw className="h-3.5 w-3.5" /></Button>
                  <Button variant="outline" size="sm" onClick={() => rotateSel(-15)} className="border-[#C8C2AC] px-0 text-xs" data-testid="studio-rotate-minus-15">-15°</Button>
                  <Button variant="outline" size="sm" onClick={() => rotateSel(15)} className="border-[#C8C2AC] px-0 text-xs" data-testid="studio-rotate-plus-15">+15°</Button>
                  <Button variant="outline" size="sm" onClick={() => rotateSel(90)} className="border-[#C8C2AC] px-0" data-testid="studio-rotate-plus-90" title="Rotate 90° right"><RotateCw className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm" onClick={() => reorder("up")} className="border-[#C8C2AC]"><ChevronUp className="h-4 w-4 mr-1" /> Front</Button>
                <Button variant="outline" size="sm" onClick={() => reorder("down")} className="border-[#C8C2AC]"><ChevronDown className="h-4 w-4 mr-1" /> Back</Button>
                <Button variant="outline" size="sm" onClick={duplicateSel} className="border-[#C8C2AC]"><Copy className="h-4 w-4 mr-1" /> Duplicate</Button>
                <Button variant="outline" size="sm" onClick={deleteSel} className="border-[#9E2A2B]/40 text-[#9E2A2B] hover:bg-[#9E2A2B]/10" data-testid="studio-delete-graphic-button"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
              </div>
            </div>
          )}

          {!submitted && (
            <div className="p-4 border-b border-[#E2DEC9]" data-testid="studio-part-color-section">
              <button
                onClick={() => setColorOpen((o) => !o)}
                className="w-full flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C867B] hover:text-[#C85A32] transition-colors"
                data-testid="studio-part-color-toggle"
                aria-expanded={colorOpen}
              >
                <span className="flex items-center gap-2"><Palette className="h-3 w-3" /> Part Color</span>
                <ChevronRight className={`h-3.5 w-3.5 transition-transform ${colorOpen ? "rotate-90" : ""}`} />
              </button>
              {colorOpen && (
                <div className="mt-3" data-testid="studio-part-color-body">
                  {activePartId && isDupTarget(activePartId) && (
                    <p className="text-xs text-[#2D6A4F] bg-[#2D6A4F]/10 border border-[#2D6A4F]/30 rounded-md px-2.5 py-2 mb-3" data-testid="studio-part-color-locked-note">
                      This panel mirrors another part. Uncheck its <b>Duplicate</b> box above to color it independently.
                    </p>
                  )}
                  <p className="text-xs text-[#8C867B] mb-3">Block-fill the active part{activePartId ? "" : " (select one above)"} with a solid color.</p>
                  <div className="grid grid-cols-8 gap-1.5 mb-3">
                    {["#1A1918","#F8F6F0","#C85A32","#D4A359","#2D6A4F","#3B6998","#9E2A2B","#3D2B1F","#EADFcf","#8C867B","#0F5257","#B0413E","#264653","#E9C46A","#6A4C93","#E76F51"].map((c) => (
                      <button
                        key={c}
                        onClick={() => setActivePartColor(c)}
                        title={c}
                        data-testid={`studio-color-swatch-${c}`}
                        className={`aspect-square rounded-md border transition-transform hover:scale-110 ${activePartId && partColors[activePartId] === c ? "ring-2 ring-[#C85A32] ring-offset-1" : "border-[#E2DEC9]"}`}
                        style={{ background: c }}
                      />
                    ))}
                  </div>
                  {recentColors.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[10px] uppercase tracking-widest text-[#8C867B] mb-1.5">Recent</p>
                      <div className="grid grid-cols-8 gap-1.5">
                        {recentColors.map((c) => (
                          <button
                            key={c}
                            onClick={() => setActivePartColor(c)}
                            title={c}
                            data-testid={`studio-recent-color-${c}`}
                            className={`aspect-square rounded-md border transition-transform hover:scale-110 ${activePartId && partColors[activePartId] === c ? "ring-2 ring-[#C85A32] ring-offset-1" : "border-[#E2DEC9]"}`}
                            style={{ background: c }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mb-3">
                    <p className="text-[10px] uppercase tracking-widest text-[#8C867B] mb-1.5">Custom Picker</p>
                    <ColorPicker
                      value={(activePartId && partColors[activePartId]) || "#C85A32"}
                      onChange={setActivePartColor}
                      onCommit={commitRecent}
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActivePartColor(null)}
                    disabled={!activePartId || !partColors[activePartId]}
                    className="w-full border-[#C8C2AC]"
                    data-testid="studio-color-clear-button"
                  >
                    Clear color
                  </Button>
                </div>
              )}
            </div>
          )}
        </aside>
      </div>

      {/* Mobile toolbar */}
      {!isDesktop && (
        <nav className="shrink-0 flex items-stretch gap-2 bg-[#161616] px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]" data-testid="studio-mobile-toolbar">
          <button onClick={() => setMobilePanel(mobilePanel === "graphics" ? null : "graphics")} data-testid="studio-mobile-tab-graphics" className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-mono uppercase tracking-wider transition-colors ${mobilePanel === "graphics" ? "bg-white/10 text-[#F8F6F0]" : "text-[#B8B2A6] hover:text-[#F8F6F0]"}`}>
            <ImageIcon className="h-4 w-4" /> Graphics
          </button>
          <button onClick={() => setMobilePanel(mobilePanel === "options" ? null : "options")} data-testid="studio-mobile-tab-options" className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-mono uppercase tracking-wider transition-colors ${mobilePanel === "options" ? "bg-white/10 text-[#F8F6F0]" : "text-[#B8B2A6] hover:text-[#F8F6F0]"}`}>
            <Layers className="h-4 w-4" /> Design Tools
          </button>
        </nav>
      )}

      {/* Mobile sheet backdrop */}
      {!isDesktop && mobilePanel && (
        <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setMobilePanel(null)} data-testid="studio-mobile-sheet-backdrop" />
      )}
    </div>
  );
}
